import { type StatsigLayerName, useLayer } from '@coursera-web/statsig';
import type { Payments_PaymentProcessorId as PaymentProcessorId } from '__generated__/graphql-types';

import isMobileApp from 'js/lib/isMobileApp';
import requestCountry from 'js/lib/requestCountry';
import { get as getUser } from 'js/lib/user';

import { useProductFeatures } from 'bundles/enroll-common/utils/productFeatureFlags';
import type { PaymentProcessorType } from 'bundles/payments-common/common/PaymentProcessorTypes';
import { IdTypes } from 'bundles/product-features';
import { isStripePaymentProcessor } from 'bundles/subscriptions-common/utils/isStripePaymentProcessor';

const LAYER_NAME: StatsigLayerName = 'revoke-access-and-limited-free-trial-layer';
const PARAM_NAME = 'revoke-and-limited-trial-variant';

export type Variant = 'control' | 'revoke-access' | 'limited-free-trial' | 'revoke-and-limited-trial';

/**
 * Reads the learner's variant of the Revoke Access and Limited Free Trial experiment. Reading the
 * layer param is itself what logs the Statsig exposure, so we skip the read (returning 'control'
 * instead) if the learner should be excluded (a pre-existing subscriber, MONETIZE-3123/3153; or
 * mobile app, MONETIZE-3163) or the caller says this render isn't a real exposure (e.g. no free
 * trial is actually on offer here). `variantOnFailure` is required so each caller states its own
 * default for when the variant can't be resolved at all (MONETIZE-3119). `shouldRecordExposure` is
 * optional and only applied when present and actually a boolean, so a caller forwarding an
 * unresolved (e.g. `undefined`) value falls back to recording the exposure rather than silently
 * inheriting whatever that unresolved value happens to be.
 * https://console.statsig.com/2aQACzHI81tdbY41wEZm3H/layers/revoke-access-and-limited-free-trial-layer
 */
export const useRevokeAccessAndLimitedTrialVariant = (options: {
  variantOnFailure: Variant;
  shouldRecordExposure?: boolean;
}): { variant: Variant; loading: boolean } => {
  const { variantOnFailure } = options;
  const shouldRecordExposure =
    options?.shouldRecordExposure !== undefined && typeof options?.shouldRecordExposure === 'boolean'
      ? options.shouldRecordExposure
      : true;
  const { features, loading, error } = useProductFeatures({
    idType: IdTypes.User,
    id: String(getUser().id),
  });
  const isExcludedFromExperiment = features.get(
    'commerceMonetization',
    'excludeLearnerSubscribedPriorToCurrentMonetizationExperiment'
  );
  const layer = useLayer(LAYER_NAME);
  if (loading) {
    return { variant: 'control', loading: true };
  }
  // Mobile is excluded from FE exposure only (MONETIZE-3163); BE item locking still
  // evaluates Statsig once mobile targeting is removed from the experiment gate.
  if (isExcludedFromExperiment || isMobileApp.get() || !shouldRecordExposure) {
    return { variant: 'control', loading: false };
  }
  // An errored exclusion check would otherwise read as a control assignment.
  if (error) {
    return { variant: variantOnFailure, loading: false };
  }
  return { variant: layer.get(PARAM_NAME, 'control') as Variant, loading: false };
};

export const useIsRevokeAccessEnabled = (
  shouldRecordExposure: boolean = true
): { isRevokeAccessEnabled: boolean; loading: boolean } => {
  // Fail open: an unresolved variant must not revoke access from a true control learner.
  const { variant, loading } = useRevokeAccessAndLimitedTrialVariant({
    variantOnFailure: 'control',
    shouldRecordExposure,
  });
  return { isRevokeAccessEnabled: variant === 'revoke-access' || variant === 'revoke-and-limited-trial', loading };
};

// Before selection, disable only when all offered processors are non-Stripe so legal copy cannot flip; non-Stripe cannot convert the trial to paid early.
export const useIsLimitedFreeTrialEnabled = (options?: {
  paymentProcessor?: PaymentProcessorType | null;
  paymentProcessors?: ReadonlyArray<PaymentProcessorId> | null;
  shouldRecordExposure?: boolean;
}): { isLimitedFreeTrialEnabled: boolean; loading: boolean } => {
  // Fail closed: an unresolved variant locks transiently rather than promising unlimited access.
  const { variant, loading } = useRevokeAccessAndLimitedTrialVariant({
    variantOnFailure: 'limited-free-trial',
    shouldRecordExposure: options?.shouldRecordExposure,
  });
  const paymentProcessor = options?.paymentProcessor;
  const paymentProcessors = options?.paymentProcessors;
  // India has no Stripe checkout rail, so it is always non-Stripe regardless of the offered processors.
  const isNonStripeCheckout =
    requestCountry.get() === 'IN' ||
    (paymentProcessors?.length
      ? paymentProcessors.every((processor) => !isStripePaymentProcessor(processor))
      : Boolean(paymentProcessor && !isStripePaymentProcessor(paymentProcessor)));
  return {
    isLimitedFreeTrialEnabled:
      !isNonStripeCheckout && (variant === 'limited-free-trial' || variant === 'revoke-and-limited-trial'),
    loading,
  };
};
